/*
  NBV2 Scroll Video (sections/nbv2-scroll-video.liquid)

  The section is taller than the viewport and its sticky child pins for the
  extra distance (assets/section-nbv2-scroll-video.css). This script turns
  that scroll distance into a 0 -> 1 progress and uses it to:

    - expand the video frame from its inset box to full screen (start of the pin)
    - scrub the video's currentTime, forwards and backwards (whole pin)
    - fade the final screen copy in (end of the pin)

  With prefers-reduced-motion the script stands down and the CSS shows the
  static final screen instead.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nbv2-scroll-video]';
  var FINAL_CLASS = 'nbv2-scroll-video--final';
  var MOBILE_QUERY = '(max-width: 749px)';

  // Portions of the pinned scroll given to each phase.
  var INTRO_END = 0.1;
  var EXPAND_END = 0.2;
  var FINAL_START = 0.85;

  // Share of the remaining distance to the target time covered per frame.
  var SCRUB_EASE = 0.2;
  // Stop short of the last frame: some browsers blank an ended video.
  var END_MARGIN = 0.05;

  function clamp(value) {
    return Math.max(0, Math.min(value, 1));
  }

  function easeOutCubic(value) {
    return 1 - Math.pow(1 - value, 3);
  }

  function init(root) {
    if (root.nbv2SvInit === true) {
      return;
    }

    var sticky = root.querySelector('[data-nbv2-sv-sticky]');

    if (!sticky) {
      return;
    }

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    root.nbv2SvInit = true;

    var intro = root.querySelector('[data-nbv2-sv-intro]');
    var desktopVideo = root.querySelector('[data-nbv2-sv-media="desktop"] video');
    var mobileVideo = root.querySelector('[data-nbv2-sv-media="mobile"] video');
    var mobile = window.matchMedia(MOBILE_QUERY);
    var video = null;
    var distance = 0;
    var frame = null;
    var scrubFrame = null;
    var targetTime = 0;
    var currentTime = 0;

    function duration() {
      var value = video ? video.duration : 0;
      return isFinite(value) && value > 0 ? value : 0;
    }

    function scrub() {
      scrubFrame = null;

      if (!duration()) {
        return;
      }

      // Let the previous seek land first, so fast scrolling doesn't queue seeks.
      if (video.seeking) {
        requestScrub();
        return;
      }

      var diff = targetTime - currentTime;

      if (Math.abs(diff) < 0.01) {
        return;
      }

      currentTime += Math.abs(diff) < 0.04 ? diff : diff * SCRUB_EASE;
      video.currentTime = currentTime;
      requestScrub();
    }

    function requestScrub() {
      if (scrubFrame === null) {
        scrubFrame = window.requestAnimationFrame(scrub);
      }
    }

    function render() {
      frame = null;

      var progress = distance > 0 ? clamp(-root.getBoundingClientRect().top / distance) : 0;
      var finalProgress = clamp((progress - FINAL_START) / (1 - FINAL_START));

      root.style.setProperty('--nbv2-sv-expand', easeOutCubic(clamp(progress / EXPAND_END)).toFixed(4));
      root.style.setProperty('--nbv2-sv-intro', (1 - clamp(progress / INTRO_END)).toFixed(4));
      root.style.setProperty('--nbv2-sv-final', finalProgress.toFixed(4));
      root.classList.toggle(FINAL_CLASS, finalProgress > 0.5);

      if (video) {
        targetTime = progress * Math.max(0, duration() - END_MARGIN);
        requestScrub();
      }
    }

    function requestRender() {
      if (frame === null) {
        frame = window.requestAnimationFrame(render);
      }
    }

    function layout() {
      distance = Math.max(0, root.offsetHeight - sticky.offsetHeight);

      // The frame starts directly under the intro copy, whatever its height.
      if (intro) {
        root.style.setProperty('--nbv2-sv-frame-top', intro.offsetHeight + 'px');
      }

      render();
    }

    // iOS won't seek a video that has never played; a muted play/pause on the
    // first touch unlocks it.
    function prime() {
      window.removeEventListener('touchstart', prime);

      if (!video) {
        return;
      }

      var primed = video;
      var played = primed.play();
      if (played && typeof played.then === 'function') {
        played
          .then(function () {
            primed.pause();
          })
          .catch(function () {});
      } else {
        primed.pause();
      }
    }

    // The mobile video, when there is one, replaces the desktop video at the
    // mobile breakpoint. Only the video in use is loaded.
    function setVideo() {
      var next = mobileVideo && mobile.matches ? mobileVideo : desktopVideo;

      if (next === video) {
        return;
      }

      if (video) {
        video.removeEventListener('loadedmetadata', requestRender);
        video.pause();
      }

      video = next;
      currentTime = 0;

      if (!video) {
        return;
      }

      video.pause();
      video.preload = 'auto';
      if (video.readyState === 0) {
        video.load();
      }
      video.addEventListener('loadedmetadata', requestRender);
      window.addEventListener('touchstart', prime, { passive: true });
      requestRender();
    }

    var observer = null;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(layout);
      observer.observe(sticky);
      if (intro) {
        observer.observe(intro);
      }
    }

    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', layout);

    mobile.addEventListener('change', setVideo);

    setVideo();
    layout();

    root.nbv2SvDestroy = function () {
      window.removeEventListener('scroll', requestRender);
      window.removeEventListener('resize', layout);
      mobile.removeEventListener('change', setVideo);
      window.removeEventListener('touchstart', prime);
      if (video) {
        video.removeEventListener('loadedmetadata', requestRender);
      }
      if (observer) {
        observer.disconnect();
      }
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      if (scrubFrame !== null) {
        window.cancelAnimationFrame(scrubFrame);
      }
      root.nbv2SvInit = false;
      root.nbv2SvDestroy = null;
    };
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    if (container.nodeType === 1 && container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }

    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(function (root) {
      if (typeof root.nbv2SvDestroy === 'function') {
        root.nbv2SvDestroy();
      }
    });
  });
})();
